
import React, { useState, useMemo } from 'react';
import { Package, Truck, CheckCircle2, Clock, MapPin, Phone, ShieldAlert, MessageCircle, ArrowRight, AlertTriangle, Calendar, Building2, Check, ExternalLink, Zap, UserCircle, Tv, Sparkles, ClipboardList } from 'lucide-react';
import { Order, OrderStatus, Client } from './types';
import { STATUS_COLORS } from './constants';
import { Card, Badge, InfoItem } from './SharedComponents';

export const SeparationPage = ({ orders, clients, onUpdateStatus, onSelectOrder, onLaunchTerminal }: any) => {
  const activeOrders = useMemo(() => {
    return orders.filter((o: Order) => 
      o.status === OrderStatus.PENDENTE || 
      o.status === OrderStatus.CONFIRMADO || 
      o.status === OrderStatus.EM_CARREGAMENTO
    ).sort((a: Order, b: Order) => {
       if (a.origem === 'CRM' && a.status === OrderStatus.PENDENTE) return -1;
       if (b.origem === 'CRM' && b.status === OrderStatus.PENDENTE) return 1;
       return a.dataEvento.localeCompare(b.dataEvento);
    });
  }, [orders]);

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-32 w-full p-6 md:p-10 lg:p-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 border-b border-gray-100 pb-8">
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-2xl text-blue-600 shadow-sm">
              <Package size={32} strokeWidth={2.5} />
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-secondary uppercase tracking-tighter italic leading-none">
              Expedição
            </h1>
          </div>
          <p className="text-gray-400 text-xs md:text-sm font-bold uppercase tracking-widest italic ml-1">Fila de preparação de materiais e despacho de ativos.</p>
        </div>
        <button 
          onClick={onLaunchTerminal}
          className="w-full md:w-auto bg-secondary text-white px-8 py-4 rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-black transition-all shadow-2xl active:scale-95 shrink-0"
        >
          <Tv size={18} /> Terminal Priorities
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {activeOrders.length > 0 ? activeOrders.map((order: Order) => (
          <SeparationCard 
            key={order.id}
            order={order} 
            client={clients.find((c: Client) => c.id === order.clientId)} 
            onUpdateStatus={onUpdateStatus}
            onSelect={onSelectOrder}
          />
        )) : (
          <div className="col-span-full p-20 md:p-32 flex flex-col items-center justify-center text-black-200 gap-6 border-4 border-dashed border-gray-100 rounded-[60px] bg-white/50">
            <Package size={80} strokeWidth={1} className="opacity-20" />
            <div className="text-center space-y-2">
              <p className="font-black text-xl uppercase tracking-[0.3em] opacity-40">Nada para expedir agora</p>
              <p className="text-xs font-bold uppercase tracking-widest opacity-30 italic">Aguardando confirmações do financeiro ou comercial.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SeparationCard = ({ order, client, onUpdateStatus, onSelect }: any) => {
  const isCrmOrigin = order.origem === 'CRM';
  const isPending = order.status === OrderStatus.PENDENTE;
  const isToday = order.dataEvento === new Date().toISOString().split('T')[0];
  
  return (
    <Card 
      className={`p-0 border-2 transition-all hover:shadow-2xl cursor-pointer overflow-hidden group w-full bg-white ${isCrmOrigin && isPending ? 'border-cyan-400 ring-4 ring-cyan-50 animate-in zoom-in-95' : isToday ? 'border-primary' : 'border-gray-100'}`}
      onClick={() => onSelect(order)}
    >
      <div className={`p-5 flex justify-between items-center ${isCrmOrigin && isPending ? 'bg-cyan-500 text-white' : isToday ? 'bg-primary text-white' : 'bg-secondary text-white'}`}>
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-md">
            {isCrmOrigin ? <Sparkles size={18} /> : <Clock size={18} />}
          </div>
          <div>
            <p className="text-[8px] font-black uppercase tracking-[0.2em] opacity-70 leading-none mb-1">
              {isCrmOrigin ? 'CRM SYNC' : 'CARGA PROGRAMADA'}
            </p>
            <h3 className="text-[13px] font-black uppercase tracking-tight leading-none">
              {order.dataEvento.split('-').reverse().join('/')} • {order.horarioEntrega}
            </h3>
          </div>
        </div>
        <Badge className="bg-white/20 text-white border-none text-[8px] px-2 py-1 font-black">OS: {order.id.split('-')[1]}</Badge>
      </div>

      <div className="p-6 space-y-6">
        <div className="min-w-0">
           <h4 className="text-base font-black text-secondary uppercase truncate tracking-tight group-hover:text-primary transition-colors">{order.nomeEvento}</h4>
           <p className="text-[10px] text-gray-400 font-bold uppercase flex items-center gap-1.5 mt-2 truncate italic">
              <MapPin size={12} className="text-primary" /> {client?.nomeLocal || 'A definir'}
           </p>
        </div>

        <div className="flex flex-col gap-3">
           <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-gray-400 px-1 border-b border-gray-50 pb-2">
              <span>Volume da OS:</span>
              <span className="text-secondary font-black">{order.materials.length} itens</span>
           </div>
           
           {isPending ? (
              <button 
                onClick={(e) => { e.stopPropagation(); onUpdateStatus(order.id, OrderStatus.CONFIRMADO); }}
                className={`w-full py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] flex items-center justify-center gap-2 shadow-xl transition-all active:scale-95 ${isCrmOrigin ? 'bg-cyan-500 text-white shadow-cyan-100' : 'bg-primary text-white shadow-primary/20'}`}
              >
                 <CheckCircle2 size={16} strokeWidth={3} /> Aceitar OS
              </button>
           ) : (
              <button 
                onClick={(e) => { e.stopPropagation(); onUpdateStatus(order.id, OrderStatus.EM_CARREGAMENTO); }}
                className="w-full py-4 bg-secondary text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] flex items-center justify-center gap-2 shadow-2xl hover:bg-black transition-all active:scale-95"
              >
                 <Truck size={16} /> Iniciar Carga
              </button>
           )}
        </div>
      </div>
    </Card>
  );
};
